<template>
  <form @submit.prevent="handleSubmit" class="space-y-6">
    <div class="space-y-4">
      <Input
        v-model="formData.email"
        type="email"
        label="Email address"
        placeholder="Enter your email address"
        required
        :error="errors.email"
        :disabled="isLoading || emailSent"
        @blur="handleEmailBlur"
      />
    </div>

    <div v-if="emailSent" class="rounded-md bg-green-50 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd"
            />
          </svg>
        </div>
        <div class="ml-3">
          <h3 class="text-sm font-medium text-green-800">Reset email sent!</h3>
          <div class="mt-2 text-sm text-green-700">
            <p>
              We've sent a password reset link to <strong>{{ formData.email }}</strong
              >. Please check your email and follow the instructions to reset your password.
            </p>
          </div>
        </div>
      </div>
    </div>

    <div>
      <Button
        v-if="!emailSent"
        type="submit"
        variant="primary"
        size="lg"
        :loading="isLoading"
        :disabled="!isFormValid"
        full-width
      >
        Send reset email
      </Button>

      <Button v-else variant="outline" size="lg" full-width @click="resetForm">
        Send another email
      </Button>
    </div>

    <div class="text-center space-y-2">
      <p class="text-sm text-gray-600">
        Remember your password?
        <button
          type="button"
          @click="$emit('login')"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Sign in
        </button>
      </p>

      <p class="text-sm text-gray-600">
        Don't have an account?
        <button
          type="button"
          @click="$emit('register')"
          class="font-medium text-blue-600 hover:text-blue-500 transition-colors"
        >
          Sign up
        </button>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
  import { ref, computed, reactive } from 'vue';
  import { Button, Input } from '@asabdev/asab-ui';
  import { useAuth } from '../composables/useAuth';
  import type { ForgotPasswordData } from '@/types/auth';

  const emit = defineEmits<{
    success: [email: string];
    error: [error: string];
    login: [];
    register: [];
  }>();

  const { forgotPassword, isLoading, error: authError, clearError } = useAuth();

  // Form state
  const emailSent = ref(false);

  // Form data
  const formData = reactive<{ email: string }>({
    email: '',
  });

  // Form validation errors
  const errors = reactive({
    email: '',
  });

  // Validation rules
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateEmail = (email: string): string => {
    if (!email) return 'Email is required';
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    return '';
  };

  // Computed properties
  const isFormValid = computed(() => {
    return formData.email && !errors.email;
  });

  // Methods
  const validateForm = (): boolean => {
    errors.email = validateEmail(formData.email);
    return !errors.email;
  };

  const handleSubmit = async () => {
    // Clear previous auth errors
    clearError();

    // Validate form
    if (!validateForm()) {
      return;
    }

    try {
      const data: ForgotPasswordData = {
        email: formData.email,
      };

      await forgotPassword(data.email);

      emailSent.value = true;
      emit('success', formData.email);
    } catch {
      const errorMessage = authError.value || 'Failed to send reset email. Please try again.';
      emit('error', errorMessage);
    }
  };

  const resetForm = () => {
    emailSent.value = false;
    clearError();
    errors.email = '';
  };

  // Real-time validation
  const handleEmailBlur = () => {
    if (formData.email) {
      errors.email = validateEmail(formData.email);
    }
  };
</script>
